// MarketFilter — Logic 5 from STRIKZA_FreeSuperTips_Intelligence_Logics.
//
// Five-mode pill toggle. Pure controlled component — caller owns the
// state and the actual filtering (use filterByMarket from
// src/agents/value-edge.ts). Zero additional API cost; the doc
// explicitly forbids implementing this as a new agent call.

import type { MarketFilterMode } from "../agents/value-edge.ts";

export interface MarketFilterProps {
  readonly value: MarketFilterMode;
  readonly onChange: (next: MarketFilterMode) => void;
  /** Optional per-mode counts shown next to each label (e.g. from a
   *  derived helper over the source list). */
  readonly counts?: Partial<Record<MarketFilterMode, number>>;
}

const MODES: ReadonlyArray<{ id: MarketFilterMode; label: string; sub: string }> = [
  { id: "all", label: "All Markets", sub: "Default — agent-ranked across every market type." },
  { id: "result", label: "Result", sub: "Match winner / 1X2." },
  { id: "goals", label: "Goals", sub: "Over / Under goal totals." },
  { id: "btts", label: "BTTS", sub: "Both Teams to Score." },
  { id: "goalscorer", label: "Goalscorer", sub: "Anytime / first / last." },
];

export function MarketFilter({ value, onChange, counts }: MarketFilterProps): JSX.Element {
  return (
    <div
      role="tablist"
      aria-label="Market filter"
      className="market-filter"
      style={{
        display: "flex",
        gap: 6,
        flexWrap: "wrap",
        margin: "12px 0",
      }}
    >
      {MODES.map((m) => {
        const active = value === m.id;
        const count = counts?.[m.id];
        return (
          <button
            key={m.id}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={`${m.label} — ${m.sub}`}
            onClick={() => {
              if (!active) onChange(m.id);
            }}
            style={{
              fontFamily: "var(--font-mono, monospace)",
              fontSize: 11,
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              color: active ? "var(--text-primary)" : "var(--text-secondary)",
              background: active
                ? "var(--accent-soft, rgba(176,124,255,0.10))"
                : "transparent",
              padding: "6px 12px",
              border: `1px solid ${active ? "var(--accent, #b07cff)" : "var(--border-faint)"}`,
              borderRadius: 999,
              cursor: active ? "default" : "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span>{m.label}</span>
            {typeof count === "number" ? (
              <span
                style={{
                  fontSize: 10,
                  color: "var(--text-tertiary)",
                  background: "var(--bg-elev-1, transparent)",
                  padding: "1px 5px",
                  borderRadius: 4,
                }}
              >
                {count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
